<script>
const MINUTE_IN_MILLI_SECONDS = 60000;
const HOUR_IN_MILLI_SECONDS = MINUTE_IN_MILLI_SECONDS * 60;
const DAY_IN_MILLI_SECONDS = HOUR_IN_MILLI_SECONDS * 24;

import { mapGetters } from 'vuex';
import {
  dynamicTime,
  dateFormatLocale,
  shortTimestamp,
  shortTimestampLocale
} from 'shared/helpers/timeHelper';

export default {
  name: 'TimeAgo',
  props: {
    isAutoRefreshEnabled: {
      type: Boolean,
      default: true,
    },
    lastActivityTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    createdAtTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    conversationId: {
      type: [String, Number],
      default: '',
    },
  },
  data() {
    return {
      lastActivityAtTimeAgo: dynamicTime(this.lastActivityTimestamp),
      createdAtTimeAgo: dynamicTime(this.createdAtTimestamp),
      timer: null,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
    }),
    lastActivityTime() {
      return shortTimestampLocale(this.$t,this.lastActivityAtTimeAgo);
    },
    createdAt() {
      const createdTimeDiff = Date.now() - this.createdAtTimestamp * 1000;
      const isBeforeAMonth = createdTimeDiff > DAY_IN_MILLI_SECONDS * 30;
      return !isBeforeAMonth
        ? `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.CREATED.LATEST')} ${
            this.createdAtTimeAgo
          }`
        : `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.CREATED.OLDEST')} ${dateFormatTranslation(
            this.createdAtTimestamp
          )}`;
    },
    lastActivity() {
      const lastActivityTimeDiff =
        Date.now() - this.lastActivityTimestamp * 1000;
      const isNotActive = lastActivityTimeDiff > DAY_IN_MILLI_SECONDS * 30;

     const convertToTime = String(this.lastActivityAtTimeAgo)
       // casos fijos
      .replace(/less than a minute ago/g, this.$t('CONVERSATION.NOW'))
      .replace(/about a minute ago/g, this.$t('CONVERSATION.A_MINUTE_AGO'))
      .replace(/an hour ago/g, this.$t('CONVERSATION.AN_HOUR_AGO'))
      .replace(/a day ago/g, this.$t('CONVERSATION.A_DAY_AGO'))
      .replace(/a month ago/g, this.$t('CONVERSATION.A_MONTH_AGO'))
      .replace(/a year ago/g, this.$t('CONVERSATION.A_YEAR_AGO'))


      // casos con números
     .replace(/about (\d+) minutes? ago/g, (_, n) => this.$t("CONVERSATION.MINUTES_AGO").replace("$[count]", n))
      .replace(/(\d+) minutes? ago/g, (_, n) => this.$t("CONVERSATION.MINUTES_AGO").replace("$[count]", n))

      .replace(/about (\d+) hours? ago/g, (_, n) => this.$t("CONVERSATION.HOURS_AGO").replace("$[count]", n))
      .replace(/(\d+) hours? ago/g, (_, n) => this.$t("CONVERSATION.HOURS_AGO").replace("$[count]", n))

      .replace(/about (\d+) days? ago/g, (_, n) => this.$t("CONVERSATION.DAYS_AGO").replace("$[count]", n))
      .replace(/(\d+) days? ago/g, (_, n) => this.$t("CONVERSATION.DAYS_AGO").replace("$[count]", n))

      .replace(/about (\d+) months? ago/g, (_, n) => this.$t("CONVERSATION.MONTHS_AGO").replace("$[count]", n))
      .replace(/(\d+) months? ago/g, (_, n) => this.$t("CONVERSATION.MONTHS_AGO").replace("$[count]", n))

      .replace(/about (\d+) years? ago/g, (_, n) => this.$t("CONVERSATION.YEARS_AGO").replace("$[count]", n))
      .replace(/(\d+) years? ago/g, (_, n) => this.$t("CONVERSATION.YEARS_AGO").replace("$[count]", n));

      const { locale } = this.getAccount(this.currentAccountId);
      const timeAgo =  !isNotActive
        ? `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY.ACTIVE')} ${
            convertToTime
          }`
        : `${this.$t(
            'CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY.NOT_ACTIVE'
          )} ${dateFormatLocale(this.lastActivityTimestamp, locale)}`;

      return timeAgo;

      },
    lastActivityDate() {
      const lastActivityMs = this.lastActivityTimestamp * 1000; // timestamp en segundos → ms

      const date = new Date(lastActivityMs);
      const now = new Date();

      // hoy
      if (date.toDateString() === now.toDateString()) {
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${hours}:${minutes}`;
      }

      // ayer
      const yesterday = new Date();
      yesterday.setDate(now.getDate() - 1);
      if (date.toDateString() === yesterday.toDateString()) {
        return this.$t('CONVERSATION.YESTERDAY');
      }

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return this.$t('CONVERSATION.DATE_FORMAT', {dd: day, mm: month, yyyy: year});

    },
    tooltipText() {
      return `${this.lastActivityDate}
              ${this.lastActivity}`;
    },
  },
  watch: {
    lastActivityTimestamp() {
      this.lastActivityAtTimeAgo = dynamicTime(this.lastActivityTimestamp);
    },
    createdAtTimestamp() {
      this.createdAtTimeAgo = dynamicTime(this.createdAtTimestamp);
    },
    conversationId() {
      // Reset display values and timer when the row is recycled to a different conversation.
      this.lastActivityAtTimeAgo = dynamicTime(this.lastActivityTimestamp);
      this.createdAtTimeAgo = dynamicTime(this.createdAtTimestamp);
      if (this.isAutoRefreshEnabled) {
        clearTimeout(this.timer);
        this.createTimer();
      }
    },
  },
  mounted() {
    if (this.isAutoRefreshEnabled) {
      this.createTimer();
    }
  },
  unmounted() {
    clearTimeout(this.timer);
  },
  methods: {
    createTimer() {
      this.timer = setTimeout(() => {
        this.lastActivityAtTimeAgo = dynamicTime(this.lastActivityTimestamp);
        this.createdAtTimeAgo = dynamicTime(this.createdAtTimestamp);
        this.createTimer();
      }, this.refreshTime());
    },
    refreshTime() {
      const timeDiff = Date.now() - this.lastActivityTimestamp * 1000;
      if (timeDiff > DAY_IN_MILLI_SECONDS) {
        return DAY_IN_MILLI_SECONDS;
      }
      if (timeDiff > HOUR_IN_MILLI_SECONDS) {
        return HOUR_IN_MILLI_SECONDS;
      }

      return MINUTE_IN_MILLI_SECONDS;
    },
  },
};
</script>

<template>
  <div
    v-tooltip.top="{
      content: tooltipText,
      delay: { show: 1000, hide: 0 },
      hideOnClick: true,
    }"
    class="ml-auto leading-4 text-xxs text-n-slate-10 hover:text-n-slate-11"
  >
    <span>{{ `${lastActivityDate} • ${lastActivityTime}` }}</span>
  </div>
</template>
